import { httpClient } from '@/core/api/http-client'
import type { TableLayout } from '@/domain/entities/table-layout.entity'
import type { TableLayoutRepository } from '@/domain/repositories/table-layout.repository'
import { unwrap } from '@/infrastructure/shared/unwrap'
import type { TableLayoutDto } from './table-layout.dto'
import { tableLayoutMapper } from './table-layout.mapper'

export class TableLayoutHttpRepository implements TableLayoutRepository {
  async get(outletId: string): Promise<TableLayout> {
    const raw = await httpClient.get<unknown>('/table-layout', {
      params: { outlet_id: outletId },
    })
    return tableLayoutMapper.toLayout(unwrap<TableLayoutDto>(raw), outletId)
  }

  async save(layout: TableLayout): Promise<TableLayout> {
    const raw = await httpClient.put<unknown>('/table-layout', tableLayoutMapper.toRequest(layout))
    return tableLayoutMapper.toLayout(unwrap<TableLayoutDto>(raw), layout.outletId)
  }

  async updateOccupancy(outletId: string, tableId: string, isOccupied: boolean): Promise<void> {
    await httpClient.patch(`/table-layout/tables/${tableId}/occupancy`, {
      outlet_id: outletId,
      is_occupied: isOccupied,
    })
  }

  async regenerateStaticQR(outletId: string, tableId: string): Promise<{ token: string }> {
    const raw = await httpClient.post<{ data: { token: string } }>(
      `/table-layout/tables/${tableId}/qr/static/regenerate`,
      { outlet_id: outletId },
    )
    return unwrap<{ table_id: string; token: string }>(raw)
  }

  async generateDynamicQR(
    outletId: string,
    tableId: string,
    durationMinutes = 120,
  ): Promise<{ token: string; expires_at: string }> {
    const raw = await httpClient.post<{ data: { token: string; expires_at: string } }>(
      `/table-layout/tables/${tableId}/qr/dynamic/generate`,
      { outlet_id: outletId, duration_minutes: durationMinutes },
    )
    return unwrap<{ table_id: string; token: string; expires_at: string }>(raw)
  }

  async invalidateDynamicQR(outletId: string, tableId: string): Promise<void> {
    await httpClient.post(`/table-layout/tables/${tableId}/qr/dynamic/invalidate`, {
      outlet_id: outletId,
    })
  }
}
